import { PlanEditGesture } from '@core/models/plan-edit-gesture.model';
import { WatchPatch, WatchPatchEvent } from '@core/models/plan-edit.model';
import { derivePins, deriveRouteStarts } from '@core/utils/plan-edit/pins';

import { PlanEditSessionActions } from './plan-edit-session.actions';
import {
  ImpactSnapshot,
  InFlightGesture,
  PlanEditImpactState,
  PlanEditSessionState,
  PlanEditState,
  PlanWatchState,
  initialPlanEditImpactState,
  initialPlanEditSessionState
} from './plan-edit.state';
import {
  EMPTY_WATCH_OVERLAY,
  applyWatchPatch,
  feedWatch,
  removedRouteUids,
  upsertedRouteUids
} from './plan-watch-overlay';

type Actions = typeof PlanEditSessionActions;
type Props<K extends keyof Actions> = Omit<ReturnType<Actions[K]>, 'type'>;

/** DL keeps this many undo checkpoints by default (`DL_PLAN_EDIT_MAX_UNDO_CHECKPOINTS`). */
const UNDO_DEPTH = 20;
/** Close reasons after which DL parks the session for a `resume` rather than ending it. */
const PARKED_CLOSE_REASONS: readonly string[] = ['UPSTREAM_LOST', 'SERVER_SHUTDOWN'];
/** The error a session that cannot be resumed ends with when its connection drops. */
export const CONNECTION_LOST = 'connection_lost';

const withSession = (
  state: PlanEditState,
  changes: Partial<PlanEditSessionState>
): PlanEditState => ({ ...state, session: { ...state.session, ...changes } });

/** A reset keeps the hand-overs: the assistant releasing one of those plans reopens edit mode. */
const resetSession = (
  state: PlanEditState,
  changes: Partial<PlanEditSessionState> = {}
): PlanEditState => ({
  ...state,
  session: {
    ...initialPlanEditSessionState,
    handedOverPlanIds: state.session.handedOverPlanIds,
    ...changes
  }
});

function mintedRouteUid(
  gesture: PlanEditGesture | undefined,
  mintedRouteUids: Record<string, string>
): string | null {
  if (gesture?.kind !== 'open-route') return null;
  return mintedRouteUids[gesture.routeRef] ?? null;
}

/**
 * The routes the session opened and removed once a state arrives. An opened route stays known
 * once learnt: a route the patch removes is marked removed, and one it upserts no longer is, so an
 * undo or a redo of either gesture shows or hides it again.
 */
function routesOf(
  session: PlanEditSessionState,
  minted: string | null,
  patch: WatchPatch
): Partial<PlanEditSessionState> {
  const upserted = upsertedRouteUids(patch);
  const removed = removedRouteUids(patch);
  if (upserted.length === 0 && removed.length === 0) return {};
  return {
    openedRouteUids:
      minted === null || session.openedRouteUids.includes(minted)
        ? session.openedRouteUids
        : [...session.openedRouteUids, minted],
    removedRouteUids: [...new Set([...session.removedRouteUids, ...removed])].filter(
      (uid) => !upserted.includes(uid)
    )
  };
}

/** The route uids with the route `gesture` opened named by its minted uid rather than its ref. */
function withMintedUid(
  uids: string[],
  gesture: PlanEditGesture,
  minted: string | null
): string[] {
  if (minted === null || gesture.kind !== 'open-route') return uids;
  return uids.map((uid) => (uid === gesture.routeRef ? minted : uid));
}

/** The session's timing status once a state arrives: its upserted routes wait for the recompute. */
function recomputing(
  session: PlanEditSessionState,
  event: WatchPatchEvent
): Partial<PlanEditSessionState> {
  if (!event.recomputePending) return {};
  return {
    recomputingRouteUids: [
      ...new Set([...session.recomputingRouteUids, ...upsertedRouteUids(event.patch)])
    ],
    timings: 'recomputing'
  };
}

const roundInProgress = (impact: PlanEditImpactState): boolean =>
  impact.baseline !== null || impact.roundUnmeasured;

/** A settled answer's findings and KPIs, or the watch's it leaves in place when it carries none. */
function settledState(event: WatchPatchEvent, watch: PlanWatchState): ImpactSnapshot {
  return {
    findings: event.findings ?? watch.findings ?? [],
    planKpis: event.planKpis ?? watch.planKpis
  };
}

/** The watch's findings and KPIs, once it holds the session's plan and owes it no snapshot. */
function watchBaseline({ watch, session }: PlanEditState): ImpactSnapshot | null {
  if (watch.planId !== session.planId || watch.awaitingSnapshot) return null;
  return { findings: watch.findings ?? [], planKpis: watch.planKpis };
}

/**
 * A round is measured from DL's last settled answer, else from the watch; with neither it goes
 * unmeasured rather than be diffed against a state DL never settled on.
 */
function roundStarted(
  state: PlanEditState,
  touchedRouteUids: string[]
): PlanEditImpactState {
  const { impact } = state.session;
  const touched = [...new Set([...impact.touchedRouteUids, ...touchedRouteUids])];
  if (roundInProgress(impact)) return { ...impact, touchedRouteUids: touched };
  const baseline = impact.settledAfter ?? watchBaseline(state);
  return {
    ...impact,
    baseline,
    roundUnmeasured: baseline === null,
    touchedRouteUids: touched
  };
}

/** The round in progress ends; measured and answered, it becomes the last round. */
function roundEnded(
  impact: PlanEditImpactState,
  after: ImpactSnapshot | null
): PlanEditImpactState {
  return {
    ...initialPlanEditImpactState,
    settledAfter: impact.settledAfter,
    lastRound:
      impact.baseline === null || after === null
        ? impact.lastRound
        : { before: impact.baseline, after, touchedRouteUids: impact.touchedRouteUids }
  };
}

/**
 * Every settled answer is what the next round is measured from; once nothing is owed or in flight
 * it is also the round's final state.
 */
function roundSettled(
  session: PlanEditSessionState,
  after: ImpactSnapshot
): PlanEditImpactState {
  const impact = { ...session.impact, settledAfter: after };
  if (
    !roundInProgress(impact) ||
    session.inFlight.length > 0 ||
    session.awaitingUndo !== null
  ) {
    return impact;
  }
  return roundEnded(impact, after);
}

export function editRequested(
  state: PlanEditState,
  { planId }: Props<'editRequested'>
): PlanEditState {
  if (state.session.mode !== 'off') return state;
  return resetSession(state, { mode: 'opening', planId });
}

export function opened(
  state: PlanEditState,
  { sessionId, resumable }: Props<'opened'>
): PlanEditState {
  if (state.session.mode !== 'opening') return state;
  return withSession(state, { mode: 'editing', sessionId, resumable });
}

/** A new gesture drops the redo tail, as DL drops its journal's. */
export function gestureSent(state: PlanEditState, gesture: InFlightGesture): PlanEditState {
  const { clientSeq, touchedRouteUids, predicted } = gesture;
  const s = state.session;
  return withSession(state, {
    inFlight: [
      ...s.inFlight,
      { clientSeq, gesture: gesture.gesture, touchedRouteUids, predicted }
    ],
    nextClientSeq: clientSeq + 1,
    applied: s.applied.slice(0, s.cursor),
    impact: roundStarted(state, touchedRouteUids)
  });
}

export function undoSent(
  state: PlanEditState,
  { clientSeq, touchedRouteUids, direction }: Props<'undoSent'>
): PlanEditState {
  return withSession(state, {
    awaitingUndo: { clientSeq, touchedRouteUids, direction },
    nextClientSeq: clientSeq + 1,
    impact: roundStarted(state, touchedRouteUids)
  });
}

/**
 * An `applied` answers either the outstanding undo or redo, which moves the cursor, or an in-flight
 * gesture, which joins the stack. Either way its state joins the watch overlay, so the session and
 * the watch of the same session show one state.
 */
export function applied(
  state: PlanEditState,
  {
    appliedClientSeq,
    revision,
    recomputePending,
    patch,
    findings,
    planKpis,
    mintedRouteUids
  }: Props<'applied'>
): PlanEditState {
  const event: WatchPatchEvent = { revision, recomputePending, patch, findings, planKpis };
  const s = state.session;
  let session = s;
  const entry = s.inFlight.find((g) => g.clientSeq === appliedClientSeq);
  // A redo mints nothing: DL replays the open under the uid its first answer already taught.
  const minted = mintedRouteUid(entry?.gesture, mintedRouteUids);
  if (s.awaitingUndo?.clientSeq === appliedClientSeq) {
    const step = s.awaitingUndo.direction === 'undo' ? -1 : 1;
    session = {
      ...s,
      cursor: s.cursor + step,
      unsavedCount: s.unsavedCount + step,
      awaitingUndo: null
    };
  } else if (entry !== undefined) {
    const stack = [
      // An undo answered after this gesture was sent moved the cursor: what it undid is gone at DL.
      ...s.applied.slice(0, s.cursor),
      {
        clientSeq: entry.clientSeq,
        gesture: entry.gesture,
        touchedRouteUids: withMintedUid(entry.touchedRouteUids, entry.gesture, minted)
      }
    ];
    const dropped = stack.slice(0, Math.max(0, stack.length - UNDO_DEPTH));
    const kept = stack.slice(dropped.length);
    session = {
      ...s,
      inFlight: s.inFlight.filter((g) => g !== entry),
      applied: kept,
      cursor: kept.length,
      unsavedCount: s.unsavedCount + 1,
      // Past the stack nothing can undo a gesture, so its pin changes join the base set.
      pinsAtSave: derivePins(s.pinsAtSave, dropped, dropped.length),
      routeStartsAtSave: deriveRouteStarts(s.routeStartsAtSave, dropped, dropped.length)
    };
  }
  const watch = feedWatch(state.watch, s.planId, event);
  const { impact } = session;
  const applying: PlanEditSessionState = {
    ...session,
    impact: {
      ...impact,
      baselineApplied: roundInProgress(impact),
      touchedRouteUids:
        entry === undefined
          ? impact.touchedRouteUids
          : withMintedUid(impact.touchedRouteUids, entry.gesture, minted)
    }
  };
  return {
    ...state,
    session: {
      ...applying,
      ...recomputing(session, event),
      ...routesOf(session, minted, patch),
      ...(recomputePending
        ? {}
        : { impact: roundSettled(applying, settledState(event, watch)) })
    },
    watch
  };
}

export function recomputed(
  state: PlanEditState,
  { revision, patch, findings, planKpis }: Props<'recomputed'>
): PlanEditState {
  const event: WatchPatchEvent = {
    revision,
    recomputePending: false,
    patch,
    findings,
    planKpis
  };
  const watch = feedWatch(state.watch, state.session.planId, event);
  const after = settledState(event, watch);
  const { impact } = state.session;
  // Until DL applies a gesture of the round, a recompute settles the state the round started from.
  const measured =
    roundInProgress(impact) && !impact.baselineApplied
      ? { ...impact, baseline: after, roundUnmeasured: false }
      : impact;
  return {
    ...state,
    session: {
      ...state.session,
      recomputingRouteUids: [],
      timings: 'settled',
      impact: roundSettled({ ...state.session, impact: measured }, after)
    },
    watch
  };
}

/**
 * A refusal leaving nothing in flight ends the round: one nothing was applied in never happened,
 * and one DL owes no recompute for ends on its last settled answer.
 */
function refusalEnded(session: PlanEditSessionState, idle: boolean): PlanEditImpactState {
  const { impact } = session;
  if (!idle || !roundInProgress(impact)) return impact;
  if (!impact.baselineApplied) return roundEnded(impact, null);
  return session.timings === 'settled' ? roundEnded(impact, impact.settledAfter) : impact;
}

/**
 * A rejection with a seq refuses that gesture, undo or redo. Without one it refuses the open while
 * opening, or the resume while disconnected: either ends edit mode, keeping the error to explain it.
 * Otherwise, during a recompute, it is the recompute failing: the routes keep their provisional
 * timings.
 */
export function rejected(
  state: PlanEditState,
  { clientSeq, errorCode, errorMessage, details }: Props<'rejected'>
): PlanEditState {
  const s = state.session;
  const gesture = s.inFlight.find((g) => g.clientSeq === clientSeq)?.gesture;
  const lastError = {
    code: errorCode,
    details,
    ...(errorMessage ? { message: errorMessage } : {}),
    ...(gesture ? { gesture } : {})
  };
  if (clientSeq !== null) {
    const inFlight = s.inFlight.filter((g) => g.clientSeq !== clientSeq);
    const awaitingUndo = s.awaitingUndo?.clientSeq === clientSeq ? null : s.awaitingUndo;
    return withSession(state, {
      inFlight,
      awaitingUndo,
      lastError,
      impact: refusalEnded(s, inFlight.length === 0 && awaitingUndo === null)
    });
  }
  if (s.mode === 'opening' || s.mode === 'disconnected') {
    return resetSession(state, { lastError });
  }
  if (s.timings === 'recomputing') {
    return withSession(state, {
      timings: 'provisional',
      recomputingRouteUids: [],
      lastError: { ...lastError, recompute: true }
    });
  }
  return withSession(state, { lastError });
}

export function saveSent(
  state: PlanEditState,
  { clientSeq }: Props<'saveSent'>
): PlanEditState {
  const { mode } = state.session;
  return withSession(state, {
    mode: mode === 'editing' ? 'saving' : mode,
    nextClientSeq: clientSeq + 1
  });
}

/**
 * DL rebases the session on the saved plan and keeps its pins and route starts; undoing past a save
 * is not offered.
 */
export function saved(state: PlanEditState): PlanEditState {
  const s = state.session;
  return withSession(state, {
    mode: s.mode === 'saving' ? 'editing' : s.mode,
    pinsAtSave: derivePins(s.pinsAtSave, s.applied, s.cursor),
    routeStartsAtSave: deriveRouteStarts(s.routeStartsAtSave, s.applied, s.cursor),
    routesOpenedAtSave: s.openedRouteUids.filter(
      (uid) => !s.removedRouteUids.includes(uid)
    ),
    applied: [],
    cursor: 0,
    unsavedCount: 0
  });
}

export function saveRejected(
  state: PlanEditState,
  { errorCode, errorMessage, details }: Props<'saveRejected'>
): PlanEditState {
  const s = state.session;
  return withSession(state, {
    mode: s.mode === 'saving' ? 'editing' : s.mode,
    lastError: {
      code: errorCode,
      details,
      ...(errorMessage ? { message: errorMessage } : {})
    }
  });
}

/** Nothing can reach DL while disconnected; it drops the parked session after its grace window. */
export function leftWhileDisconnected(state: PlanEditState): PlanEditState {
  return state.session.mode === 'disconnected' ? resetSession(state) : state;
}

export function closeSent(state: PlanEditState): PlanEditState {
  return state.session.mode === 'off' ? state : withSession(state, { mode: 'closing' });
}

/**
 * An open or a close in flight, or a session DL cannot resume, has nothing to come back to: it ends
 * here, so Edit works again. A token refusal also drops the socket with no session open; that
 * changes nothing here.
 */
export function connectionLost(state: PlanEditState): PlanEditState {
  switch (state.session.mode) {
    case 'editing':
    case 'saving':
      if (!state.session.resumable) {
        return resetSession(state, { lastError: { code: CONNECTION_LOST, details: {} } });
      }
      return withSession(state, { mode: 'disconnected' });
    case 'opening':
    case 'closing':
      return resetSession(state);
    default:
      return state;
  }
}

/**
 * DL resumes with no undo history, so the stack folds into the pins and route starts held at save,
 * which DL keeps through a park. A gesture in flight at the drop may or may not have landed: its
 * prediction goes, and the stored plan plus the resumed patch is the whole session.
 */
export function resumed(
  state: PlanEditState,
  { sessionId, revision, recomputePending, patch, findings, planKpis }: Props<'resumed'>
): PlanEditState {
  const s = state.session;
  // A resume answered after the session ended locally must not bring it back.
  if (s.mode !== 'disconnected') return state;
  const event: WatchPatchEvent = { revision, recomputePending, patch, findings, planKpis };
  const session: PlanEditSessionState = {
    ...s,
    mode: 'editing',
    sessionId,
    inFlight: [],
    applied: [],
    cursor: 0,
    pinsAtSave: derivePins(s.pinsAtSave, s.applied, s.cursor),
    routeStartsAtSave: deriveRouteStarts(s.routeStartsAtSave, s.applied, s.cursor),
    awaitingUndo: null,
    recomputingRouteUids: [],
    timings: 'settled'
  };
  const watch =
    s.planId === null || s.planId !== state.watch.planId
      ? state.watch
      : {
          ...state.watch,
          revision,
          recomputePending,
          recomputeFailed: false,
          findings,
          planKpis: planKpis ?? state.watch.planKpis,
          overlay: applyWatchPatch(EMPTY_WATCH_OVERLAY, patch)
        };
  return {
    ...state,
    session: {
      ...session,
      ...recomputing(session, event),
      impact: {
        ...initialPlanEditImpactState,
        settledAfter: recomputePending ? null : settledState(event, watch)
      }
    },
    watch
  };
}

/**
 * A reloaded tab's stashed session comes back disconnected, as a dropped socket leaves it, so the
 * resume goes out the same way; a session already in the tab wins.
 */
export function restoredAfterReload(
  state: PlanEditState,
  { snapshot }: Props<'restoredAfterReload'>
): PlanEditState {
  if (state.session.mode !== 'off') return state;
  return resetSession(state, {
    mode: 'disconnected',
    planId: snapshot.planId,
    sessionId: snapshot.sessionId,
    resumable: true,
    unsavedCount: snapshot.unsavedCount,
    pinsAtSave: snapshot.pins,
    routeStartsAtSave: snapshot.routeStarts,
    openedRouteUids: snapshot.openedRouteUids,
    removedRouteUids: snapshot.removedRouteUids,
    routesOpenedAtSave: snapshot.routesOpenedAtSave
  });
}

export function closed(state: PlanEditState, { reason }: Props<'closed'>): PlanEditState {
  const s = state.session;
  if (s.mode !== 'off' && s.resumable && PARKED_CLOSE_REASONS.includes(reason)) {
    return withSession(state, { mode: 'disconnected' });
  }
  return resetSession(state);
}

export function handedOver(
  state: PlanEditState,
  { planId }: Props<'handedOver'>
): PlanEditState {
  const ids = state.session.handedOverPlanIds;
  return ids.includes(planId)
    ? state
    : withSession(state, { handedOverPlanIds: [...ids, planId] });
}

export function handedBack(
  state: PlanEditState,
  { planId }: Props<'handedBack'>
): PlanEditState {
  return withSession(state, {
    handedOverPlanIds: state.session.handedOverPlanIds.filter((id) => id !== planId)
  });
}
